import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import {
  CurrencyCircleDollar, CheckCircle, XCircle, Clock, ArrowClockwise, MagnifyingGlass,
  Coins, CurrencyDollar, FileCsv, FilePdf, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_LABEL = { "1m": "1M", "3m": "3M", "6m": "6M", "12m": "12M" };

const STATUS_PILL = {
  paid_pending_approval: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  active_unverified: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  expired: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30",
  rejected: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

const STATUS_LABEL = {
  paid_pending_approval: "Active (Unverified)",
  active_unverified: "Active (Unverified)",
  verified: "Verified",
  active: "Verified",
  expired: "Expired",
  rejected: "Rejected",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function BrokerPill({ name }) {
  return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border bg-white/5 text-zinc-300 border-white/10">{name || "-"}</span>;
}

function AcctBadge({ type }) {
  const isC = type === "cent";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${isC ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>
      {isC ? <Coins size={9} weight="fill" /> : <CurrencyDollar size={9} weight="fill" />}
      {type.toUpperCase()}
    </span>
  );
}

export default function MT5Approvals() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [query, setQuery] = useState("");
  const [fType, setFType] = useState("");
  const [fPlan, setFPlan] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [showPw, setShowPw] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fType) params.append("account_type", fType);
      if (fPlan) params.append("plan", fPlan);
      if (fStatus) params.append("status", fStatus);
      const r = await axios.get(`${API}/admin/mt5/report?${params.toString()}`, { headers: getAuthHeaders() });
      setRows(r.data.rows || []);
    } catch (err) {
      toast.error("Failed to load MT5 report");
    } finally {
      setLoading(false);
    }
  }, [fType, fPlan, fStatus]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleActivate = async (row) => {
    if (!isMaster) return;
    setProcessing((p) => ({ ...p, [row.user_id + row.account_type]: "activate" }));
    try {
      await axios.post(`${API}/admin/mt5/activate/${row.user_id}/${row.account_type}`, {}, { headers: getAuthHeaders() });
      toast.success(`Activated ${row.account_type.toUpperCase()} for ${row.name}`);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activate failed");
    } finally {
      setProcessing((p) => { const n = { ...p }; delete n[row.user_id + row.account_type]; return n; });
    }
  };

  const handleReject = async (row) => {
    if (!isMaster) return;
    const reason = window.prompt(`Reject ${row.account_type.toUpperCase()} for ${row.name}? Optional reason:`);
    if (reason === null) return;
    setProcessing((p) => ({ ...p, [row.user_id + row.account_type]: "reject" }));
    try {
      await axios.post(`${API}/admin/mt5/reject/${row.user_id}/${row.account_type}`, { reason }, { headers: getAuthHeaders() });
      toast.success(`Rejected ${row.account_type.toUpperCase()}`);
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally {
      setProcessing((p) => { const n = { ...p }; delete n[row.user_id + row.account_type]; return n; });
    }
  };

  const handleDownload = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (fType) params.append("account_type", fType);
      if (fPlan) params.append("plan", fPlan);
      if (fStatus) params.append("status", fStatus);
      const res = await axios.get(`${API}/admin/mt5/report/export?${params.toString()}`, {
        headers: getAuthHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mt5_report.${format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const filtered = rows.filter((r) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.name, r.email, r.broker_name, r.mt5_login_id, r.mt5_server].some((v) => (v || "").toLowerCase().includes(q));
  });

  const clearFilters = fType || fPlan || fStatus;

  return (
    <div className="space-y-5" data-testid="mt5-approvals-section">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,255,0.1)] flex items-center justify-center">
            <CurrencyCircleDollar size={20} weight="duotone" className="text-[#00E5FF]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              MT5 Subscriptions Report
            </h2>
            <p className="text-xs text-[#A1A1AA]">
              {isMaster ? "Master view — full access including passwords" : "Read-only — passwords hidden"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isMaster && (
            <button
              onClick={() => setShowPw(!showPw)}
              data-testid="mt5-toggle-password"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-white/10 text-[#A1A1AA] hover:text-white transition-all"
            >
              {showPw ? <EyeSlash size={12} /> : <Eye size={12} />}
              {showPw ? "Hide" : "Show"} passwords
            </button>
          )}
          <button onClick={fetchRows} data-testid="mt5-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/20 transition-all">
            <ArrowClockwise size={12} /> Refresh
          </button>
          <button onClick={() => handleDownload("csv")} data-testid="mt5-export-csv"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all">
            <FileCsv size={12} /> CSV
          </button>
          <button onClick={() => handleDownload("pdf")} data-testid="mt5-export-pdf"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 transition-all">
            <FilePdf size={12} /> PDF
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid="mt5-filters">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input
            placeholder="Search name, broker, login..." value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="mt5-search-input"
            className="pl-7 pr-3 py-1.5 rounded-md text-xs bg-[#0F0F0F] border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00E5FF]/40 w-56"
          />
        </div>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="mt5-filter-type"
          className="px-2 py-1.5 rounded-md text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40">
          <option value="">All accounts</option>
          <option value="cent">CENT only</option>
          <option value="usd">USD only</option>
        </select>
        <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} data-testid="mt5-filter-plan"
          className="px-2 py-1.5 rounded-md text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40">
          <option value="">All plans</option>
          <option value="1m">1 Month</option>
          <option value="3m">3 Months</option>
          <option value="6m">6 Months</option>
          <option value="12m">12 Months</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="mt5-filter-status"
          className="px-2 py-1.5 rounded-md text-xs bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#00E5FF]/40">
          <option value="">All statuses</option>
          <option value="active_unverified">Active (Unverified)</option>
          <option value="verified">Verified</option>
          <option value="expired">Expired</option>
          <option value="rejected">Rejected</option>
        </select>
        {clearFilters && (
          <button onClick={() => { setFType(""); setFPlan(""); setFStatus(""); }}
            data-testid="mt5-clear-filters"
            className="px-2 py-1 rounded-md text-[10px] text-[#00E5FF] hover:bg-[#00E5FF]/10">
            Clear
          </button>
        )}
        <span className="ml-auto text-[10px] text-[#A1A1AA]">{filtered.length} row{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden" data-testid="mt5-approvals-table">
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="mt5-empty-state">
            <CheckCircle size={32} className="text-emerald-500/40 mx-auto mb-2" weight="duotone" />
            <p className="text-sm text-[#A1A1AA]">No MT5 subscriptions match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#A1A1AA] border-b border-white/5 bg-[#0A0A0A]">
                  <th className="px-4 py-3 font-semibold">Affiliate</th>
                  <th className="px-3 py-3 font-semibold">Type</th>
                  <th className="px-3 py-3 font-semibold">Broker</th>
                  <th className="px-3 py-3 font-semibold">Login</th>
                  {isMaster && <th className="px-3 py-3 font-semibold">Password</th>}
                  <th className="px-3 py-3 font-semibold">Server</th>
                  <th className="px-3 py-3 font-semibold">Plan</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Expires</th>
                  {isMaster && <th className="px-4 py-3 font-semibold text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const key = r.user_id + r.account_type;
                  const state = processing[key];
                  const pill = STATUS_PILL[r.status] || "bg-white/5 text-zinc-400 border-white/10";
                  return (
                    <tr key={key + i} data-testid={`mt5-row-${key}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm text-white font-medium" data-testid={`mt5-name-${key}`}>{r.name || "Unnamed"}</div>
                          <div className="text-[10px] text-[#A1A1AA]">{r.email}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3" data-testid={`mt5-type-${key}`}><AcctBadge type={r.account_type} /></td>
                      <td className="px-3 py-3"><BrokerPill name={r.broker_name} /></td>
                      <td className="px-3 py-3"><span className="text-xs text-white font-mono" data-testid={`mt5-login-${key}`}>{r.mt5_login_id || "-"}</span></td>
                      {isMaster && (
                        <td className="px-3 py-3">
                          <span className="text-xs text-amber-400 font-mono" data-testid={`mt5-pw-${key}`}>
                            {showPw ? r.mt5_password : "••••••"}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-3"><span className="text-[11px] text-[#A1A1AA] font-mono">{r.mt5_server || "-"}</span></td>
                      <td className="px-3 py-3">
                        <div>
                          <div className="text-xs text-white font-bold" data-testid={`mt5-plan-${key}`}>{PLAN_LABEL[r.plan] || r.plan}</div>
                          <div className="text-[10px] text-[#A1A1AA] font-mono">₹{r.plan_fee_paid || 0}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${pill}`} data-testid={`mt5-status-${key}`}>
                          {STATUS_LABEL[r.status] || r.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-3 py-3"><span className="text-[11px] text-[#A1A1AA]">{fmtDate(r.expires_at)}</span></td>
                      {isMaster && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {(r.status === "paid_pending_approval" || r.status === "active_unverified") && (
                              <>
                                <button onClick={() => handleActivate(r)} disabled={!!state} data-testid={`mt5-activate-${key}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-40">
                                  <CheckCircle size={11} weight="bold" />
                                  {state === "activate" ? "..." : "Verify"}
                                </button>
                                <button onClick={() => handleReject(r)} disabled={!!state} data-testid={`mt5-reject-${key}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 disabled:opacity-40">
                                  <XCircle size={11} weight="bold" /> Reject
                                </button>
                              </>
                            )}
                            {(r.status === "active" || r.status === "verified") && <span className="text-[10px] text-emerald-400">✓ Verified</span>}
                            {r.status === "expired" && <span className="text-[10px] text-zinc-500">Ended</span>}
                            {r.status === "rejected" && <span className="text-[10px] text-rose-400">Rejected</span>}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!isMaster && (
        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-center gap-2">
          <Clock size={14} className="text-amber-400" />
          <p className="text-[11px] text-amber-200">Slave admins have read-only access. Contact the Master Admin to activate accounts.</p>
        </div>
      )}
    </div>
  );
}
